'use client';

/**
 * Waitlist Reporting Widget — total waitlisted, courses by demand, wait times.
 * Feature: 014-customer-enrolment-platform (Phase 5, T089)
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@kit/ui/card';
import { Clock, Users } from 'lucide-react';

import type { WaitlistMetrics } from '../_lib/types';

interface WaitlistReportProps {
  waitlist: WaitlistMetrics;
}

export function WaitlistReport({ waitlist }: WaitlistReportProps) {
  if (waitlist.totalWaitlisted === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-orange-500" />
            Waitlist
          </CardTitle>
          <CardDescription>No waitlisted students in this period</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            When courses are full, students will be added to the waitlist. Waitlist demand and resolution times will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-orange-500" />
          Waitlist
        </CardTitle>
        <CardDescription>
          {waitlist.totalWaitlisted} student{waitlist.totalWaitlisted !== 1 ? 's' : ''} waitlisted
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
            <Users className="h-5 w-5 text-orange-500" />
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums">{waitlist.totalWaitlisted}</p>
            <p className="text-xs text-muted-foreground">Total waitlisted</p>
          </div>
        </div>

        {/* Courses by demand */}
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Courses by Demand
          </h4>
          <div className="space-y-2">
            {waitlist.courses.map((course, index) => (
              <div
                key={course.courseInstanceId ?? index}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {course.courseName ?? 'Unknown Course'}
                  </p>
                  {course.avgWaitHours > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Avg wait: {course.avgWaitHours < 24
                        ? `${course.avgWaitHours}h`
                        : `${Math.round(course.avgWaitHours / 24)}d`}
                    </p>
                  )}
                </div>
                <span className="ml-2 shrink-0 text-sm font-semibold tabular-nums">
                  {course.waitlistCount}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
